import React, { useContext, useEffect } from "react";
//
import { useIntl } from "react-intl";
//
import fireDb from "./utils";
import { StoreContext } from "../../../store/StoreProvider";
import { isEmpty } from "../../../common/utils";
import Act from "../../../store/actions";
import role from "../../../config/App-role";
import urls from "../../project/ApiUrls";

import { getGroupsInfo } from "../../../pages/sna/service/userGroupService";

const RealtimeListener = () => {
  //
  const intl = useIntl();
  //
  const [state, dispatch] = useContext(StoreContext);
  const { user } = state;
  const { uid, userInfo, group, groups, refreshUserGroups } = user;

  const startRefreshUserGroups = () => {
    dispatch({
      type: Act.USER_REFRESH_USER_GROUPS,
      payload: true,
    });
  };

  const stopRefreshUserGroups = () => {
    dispatch({
      type: Act.USER_REFRESH_USER_GROUPS,
      payload: false,
    });
  };

  const addDefaultGroupToUser =
    (groupKey) =>
    ({ uid: _uid, userData, userGroups }) => {
      // 儲存 user data
      if (_uid) {
        const defaultGroupData = {
          uid: groupKey,
          isMember: true,
          role: role.admin,
          // 區分正式站/測試站
          appWebMode: process.env.REACT_APP_WEB_MODE,
        };
        fireDb.updateUserGroups({
          userId: user?.uid,
          groupId: groupKey,
          data: defaultGroupData,
          onSuccess: () => {
            // 儲存 groups 至 store
            dispatch({
              type: Act.USER_SET_GROUPS,
              payload: {
                ...(userData?.groups || userGroups || {}),
                [groupKey]: defaultGroupData,
              },
            });
            // success message
            dispatch({
              type: Act.COMMON_SET_SUCCESS_MESSAGE,
              payload: {
                open: true,
                content: intl.formatMessage({
                  id: "realtime.create.default.group.success",
                  defaultMessage: "成功建立預設團隊",
                }),
              },
            });
          },
          onError: (error) => {
            // error message
            dispatch({
              type: Act.COMMON_SET_ERROR_MESSAGE,
              payload: {
                open: true,
                content: error.message,
              },
            });
          },
        });
      }
    };

  const addUserDefaultGroupToGroups = ({
    uid: _uid,
    userData,
    userGroups,
    onSuccess,
  }) => {
    fireDb.createGroup({
      data: {
        displayName: intl.formatMessage({
          id: "realtime.default.group",
          defaultMessage: "預設團隊",
        }),
        members: {
          [_uid]: {
            isMember: true,
            role: role.admin,
          },
        },
      },
      onSuccess: (groupKey) => {
        if (onSuccess) onSuccess(groupKey)({ uid: _uid, userData, userGroups });
      },
      onError: (err) => null,
    });
  };

  // 取得 groups 及 current group 資訊
  // _groups: realtime-db/users/uid/groups
  const fetchGroupsAndGroupInfo = (_groups) => {
    const newGroups = Object.entries(_groups)
      .filter(([groupKey, valObj]) => valObj.isMember)
      // 區分正式站/測試站的資料
      .filter(
        ([groupKey, valObj]) =>
          valObj.appWebMode === process.env.REACT_APP_WEB_MODE
      )
      .reduce((acc, [groupKey, valObj]) => {
        acc[groupKey] = valObj;
        return acc;
      }, {});
    // 取得使用者在各 group 中的 role
    fireDb.getUserRoleGroupInfoInGroups({
      userId: user?.uid,
      groupIds: Object.keys(newGroups),
      onSuccess: (userRoleInGroups) => {
        const newGroupsWithRole = Object.entries(newGroups).reduce(
          (acc, [groupKey, valObj]) => {
            const roleInfoFind = userRoleInGroups.find(
              (item) => item.groupId === groupKey
            );
            acc[groupKey] = {
              ...valObj,
            };
            if (roleInfoFind) {
              acc[groupKey].role = roleInfoFind.role;
              acc[groupKey].displayName = roleInfoFind.displayName;
              acc[groupKey].files = roleInfoFind.files;
            }
            return acc;
          },
          {}
        );
        // 儲存 groups 至 store
        dispatch({
          type: Act.USER_SET_GROUPS,
          payload: {
            ...newGroupsWithRole,
          },
        });
      },
      onError: (err) => {
        // 儲存 groups 至 store
        dispatch({
          type: Act.USER_SET_GROUPS,
          payload: {
            ...newGroups,
          },
        });
      },
    });

    // 從 groups 取出其中一個 group, fetch realtime 取得 group 基本資料
    let currentGroup = Object.entries(newGroups).map(
      ([groupKey, groupObjVal]) => ({
        uid: groupKey,
      })
    )[0];
    // 如果 store 已有 current group, 則使用該 group
    if (group?.uid) {
      currentGroup = group;
    }

    const getGroupsInfoSuccess = (userGroupsFind) => {
      const curGroupRole = (Object.values(userGroupsFind)[0] || {})?.members?.[
        uid
      ]?.role;
      dispatch({
        type: Act.USER_SET_CUR_GROUP,
        payload: {
          ...(Object.values(userGroupsFind)[0] || {}),
          role: curGroupRole,
        },
      });
    };
    const getGroupsInfoError = (err) => {
      dispatch({
        type: Act.COMMON_SET_ERROR_MESSAGE,
        payload: {
          open: true,
          content: err.message,
        },
      });
    };
    getGroupsInfo(
      [currentGroup?.uid],
      getGroupsInfoSuccess,
      getGroupsInfoError
    );
  };

  const handleNewUserInfo = () => {
    const newUserData = {
      info: {
        ...userInfo,
        uid,
        role: role.reader,
        locale: urls.locale_lang.LOCALE_DEFAULT,
      },
    };
    // 為新的使用者在 realtime db 建立基本資料,並建立預設團隊
    fireDb.updateUser({
      uid,
      data: newUserData,
      onSuccess: () => {
        dispatch({
          type: Act.USER_SET_ROLE,
          payload: role.reader,
        });
      },
      onError: (error) => {
        dispatch({
          type: Act.COMMON_SET_ERROR_MESSAGE,
          payload: {
            open: true,
            content: error.message,
          },
        });
      },
    });
  };

  const handleUserSetRole = () => {
    const newData = {
      role: role.reader,
    };
    // 為新的使用者在 realtime db 建立基本資料,並建立預設團隊
    fireDb.updateUserRole({
      uid,
      data: newData,
      onSuccess: () => {
        dispatch({
          type: Act.USER_SET_ROLE,
          payload: role.reader,
        });
      },
      onError: (error) => {
        dispatch({
          type: Act.COMMON_SET_ERROR_MESSAGE,
          payload: {
            open: true,
            content: error.message,
          },
        });
      },
    });
  };

  // realtime get user info
  const getUserInfoData = () => {
    const onSuccess = (info) => {
      if (isEmpty(info)) return;
      if (info?.role) {
        // 使用者可以有多個角色
        dispatch({
          type: Act.USER_SET_ROLE,
          payload: info.role,
        });
        // fixme: 要移除舊的 connection 資料
        // fireDb.updateUserConnections({ uid });
      } else {
        handleUserSetRole();
      }
    };
    const onNoData = () => {
      // handle new user info
      handleNewUserInfo();
    };
    // realtime get user info
    fireDb.getUserInfoRt({ uid, onSuccess, onNoData });
  };

  // realtime get user info
  useEffect(() => {
    if (uid) {
      getUserInfoData();
    }
  }, [uid]);

  // 建立預設團隊
  const handleCreateDefaultGroup = (userGroups) => {
    addUserDefaultGroupToGroups({
      uid,
      userGroups,
      onSuccess: addDefaultGroupToUser,
    });
  };

  // realtime get user groups
  const getUserGroupsData = () => {
    const onSuccess = async (userGroups) => {
      let addDefaultGroupOn = false;
      // 使用者有建立 groups
      const inGroups = Object.entries(userGroups || {})
        .filter(([key, obj]) => obj.isMember)
        // 區分正式站/測試站
        .filter(
          ([key, obj]) => obj.appWebMode === process.env.REACT_APP_WEB_MODE
        );
      if (inGroups.length === 0) {
        addDefaultGroupOn = true;
      }
      if (addDefaultGroupOn) {
        // 如果使用者自己刪除團隊, 則不會幫他建立預設團隊
        // handleCreateDefaultGroup(userGroups);
      } else {
        // 取得 groups 及 current group 資訊
        fetchGroupsAndGroupInfo(userGroups);
      }
    };
    const onNoData = () => {
      // 建立預設團隊
      handleCreateDefaultGroup();
    };
    fireDb.getUserGroupsRt({ uid, onSuccess, onNoData });
  };

  useEffect(() => {
    if (uid) {
      startRefreshUserGroups();
    }
  }, [uid]);

  useEffect(() => {
    if (refreshUserGroups) {
      stopRefreshUserGroups();
      // realtime get user groups
      getUserGroupsData();
    }
  }, [refreshUserGroups]);

  useEffect(() => {
    // realtime 讀取 firebase 的 module setting
    const getModuleSetting = () => {
      const onSuccess = (_moduleSetting) => {
        if (_moduleSetting) {
          dispatch({
            type: Act.COMMON_MODULE_SETTING,
            payload: _moduleSetting,
          });
        }
      };
      const onNoData = () => {
        dispatch({
          type: Act.COMMON_MODULE_SETTING,
          payload: null,
        });
      };
      fireDb.getModulesRt({ onSuccess, onNoData });
    };
    if (uid) {
      getModuleSetting();
    }
  }, [uid]);

  return null;
};

export default RealtimeListener;
